'use client';
import React, { useState } from 'react';
import Image from 'next/image';

/**
 * Компонент ContactForm для створення форми зворотного зв'язку
 * @param {string} title - Заголовок форми (опціонально)
 * @param {string} subtitle - Підзаголовок форми (опціонально)
 * @param {boolean} hasCaptcha - Чи відображати капчу (імітація)
 * @param {Function} onSubmit - Функція обробки відправлення форми
 * @param {string} submitButtonText - Текст кнопки відправлення (за замовчуванням "Надіслати")
 * @param {string[]} services - Масив послуг для вибору у формі
 */
export default function ContactForm ({ 
  title = "Зв'яжіться з нами", 
  subtitle = "Наш менеджер зв'яжеться з вами у найкоротший термін", 
  hasCaptcha = true,
  onSubmit,
  submitButtonText = "Надіслати",
  services = [
    "Купівля металопрокату",
    "Продаж металобрухту",
    "Послуги різання металу",
    "Доставка металопрокату",
    "Консультація спеціаліста"
  ]
}) {
  // Стан форми
  const [formState, setFormState] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: '',
    policy: false
  });
  
  // Стан помилок
  const [errors, setErrors] = useState({});
  
  // Стан відправки
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // Обробка змін полів форми
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Очищаємо помилку поля при зміні
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  
  // Валідація форми
  const validateForm = () => {
    const newErrors = {};
    
    // Перевіряємо ім'я
    if (!formState.name.trim()) {
      newErrors.name = "Ім'я обов'язкове";
    }
    
    // Перевіряємо телефон
    if (!formState.phone.trim()) {
      newErrors.phone = "Телефон обов'язковий";
    } else if (!/^[\d\s\-\+\(\)]{10,15}$/.test(formState.phone)) {
      newErrors.phone = "Невірний формат телефону";
    }
    
    // Перевіряємо email, якщо введений
    if (formState.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      newErrors.email = "Невірний формат email";
    }
    
    // Перевіряємо прийняття політики
    if (!formState.policy) {
      newErrors.policy = "Необхідно погодитись з політикою конфіденційності";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Обробка відправки форми
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Перевіряємо валідність
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Якщо передана функція onSubmit, викликаємо її
      if (typeof onSubmit === 'function') {
        await onSubmit(formState);
      } else {
        // Імітуємо відправку форми
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
      
      // Успішна відправка
      setSubmitted(true);
      
      // Очищаємо форму
      setFormState({
        name: '',
        phone: '',
        email: '',
        service: '',
        message: '',
        policy: false
      });
      
    } catch (error) {
      // Обробка помилки
      setSubmitError(error.message || 'Помилка при відправленні форми');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="bg-white rounded-lg shadow-md p-6 my-8">
      {/* Заголовок форми */}
      <div className="text-center mb-8">
        <h3 className="text-2xl font-semibold text-gray-800">{title}</h3>
        {subtitle && <p className="text-gray-600 mt-2">{subtitle}</p>}
      </div>
      
      {submitted ? (
        /* Форма успішно відправлена */
        <div className="text-center py-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h4 className="text-xl font-medium text-gray-800 mb-2">Ваше повідомлення відправлено!</h4>
          <p className="text-gray-600">
            Дякуємо за ваше звернення. Наші спеціалісти зв&apos;яжуться з вами найближчим часом.
          </p>
          <button
            type="button"
            className="mt-6 px-6 py-2 text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 transition-colors"
            onClick={() => setSubmitted(false)}
          >
            Відправити ще одне повідомлення
          </button>
        </div>
      ) : (
        /* Форма для заповнення */
        <form onSubmit={handleSubmit} noValidate>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Ім'я */}
            <div className="col-span-1">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Ім&apos;я <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formState.name}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  errors.name ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'
                }`}
                placeholder="Введіть ваше ім'я"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>
            
            {/* Телефон */}
            <div className="col-span-1">
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Телефон <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formState.phone}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  errors.phone ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'
                }`}
                placeholder="+380 XX XXX XX XX"
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
              )}
            </div>
            
            {/* Email */}
            <div className="col-span-1">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formState.email}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  errors.email ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'
                }`}
                placeholder="example@email.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>
            
            {/* Послуга */}
            <div className="col-span-1">
              <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-1">
                Послуга, що вас цікавить
              </label>
              <div className="relative">
                <select
                  id="service"
                  name="service"
                  value={formState.service}
                  onChange={handleChange}
                  className="appearance-none w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  <option value="">Оберіть послугу</option>
                  {services.map((service, index) => (
                    <option key={index} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
            
            {/* Повідомлення */}
            <div className="col-span-1 md:col-span-2">
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                Повідомлення
              </label>
              <textarea
                id="message"
                name="message"
                value={formState.message}
                onChange={handleChange}
                rows="4"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="Опишіть ваш запит детальніше..."
              ></textarea>
            </div>
            
            {/* Капча (імітація) */}
            {hasCaptcha && (
              <div className="col-span-1 md:col-span-2">
                <div className="border border-gray-200 rounded-md p-3 bg-gray-50 flex justify-between items-center">
                  <div className="flex items-center">
                    <div className="w-5 h-5 border border-gray-300 rounded mr-2 flex items-center justify-center bg-white">
                      <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm text-gray-700">Я не робот</span>
                  </div>
                  <div className="flex items-center">
                    <Image 
                      src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoMzJ2MzJIMHoiLz48cGF0aCBkPSJNMjcuNzM3IDkuOTc5aC03LjQyMlY3LjgzN2g3LjQyMnYyLjE0MnptLTkuNzkgMGgtNy40MjF2LS4wM0gxMC41MjZWNy44MDhoNy4wODV2LS4xNWg0Ljc2MnYuMTV2Mi4xNzF6TTIwLjUyNSAxMmgtNC4wOTR2Mi4yNjNoNC4wOTNWMTJ6bS02LjQzNiAwSDUuMzM0VjkuODloOC43NTV2Mi4xMTF6bTIuMzQyIDIuNzcxaC00LjI4N3YtLjAzSDEwLjE0NVYxMi42aDUuNTVWMTZoLjczNnYtMS4yMjl6bTAgMy4wODZoLTQuMjg3djQuMjg2aC00LjI4NnYtNC4yODZIMy41NzJWMTMuNWwuMDYtLjE4OC4yMjYuMDc0LjMxMS4wOTUuMjA2LjA2M3YtLjA0M2guOTQydi4wNThsLjM2LjA1OC40MjcuMDYyaC4yMzF2LS4xMzdoLjgwNHYuMTM3aC4yMWwuNzkyLS4xMjZ2LS4wMDZoMS4xNjJ2LjAwNmwuNDg1LjA2OGguMTRWMTMuNWguODk3di4wMjdoLjE2bC43OS4xMDN2LS4wMTZoMS40MnYuMzM3bC4wNDItLjAwNWg4LjE0MnYyLjA0MnpNMTYgMTcuNDIzdjQuNDI0aC00LjQyM3YtNC40MjRIMTZ6bS4wMTYtMTQuMTFsNC4yMiA0LjE3Mkg3Ljk3N2w0LjAxNi00LjE3M2g0LjAyM3oiIGZpbGw9IiM3MzczNzMiLz48L2c+PC9zdmc+" 
                      alt="CAPTCHA logo"
                      width={32}
                      height={32}
                      className="h-8 w-8"
                    />
                    <span className="text-xs text-gray-500 ml-1">reCAPTCHA</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Політика конфіденційності */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    id="policy"
                    name="policy"
                    checked={formState.policy}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>
                <label htmlFor="policy" className="ml-3 text-sm text-gray-600">
                  Я згоден з <a href="/privacy-policy" className="text-blue-600 hover:underline">політикою конфіденційності</a> та даю згоду на обробку моїх персональних даних
                </label>
              </div>
              {errors.policy && (
                <p className="mt-1 text-sm text-red-600">{errors.policy}</p>
              )}
            </div>
          </div>
          
          {/* Помилка відправки */}
          {submitError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
              {submitError}
            </div>
          )}
          
          {/* Кнопка відправки */}
          <div className="mt-6 text-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-6 py-3 rounded-md text-white font-medium ${
                isSubmitting 
                  ? 'bg-blue-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'
              } transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Відправляємо...
                </span>
              ) : (
                submitButtonText
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
